import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useEtlynCms } from "@etlyn/etlyn-cms";

/* ═══════════════════════════════════════════════════════════ */
/*  FOOTER — light, minimal 4-column layout                    */
/* ═══════════════════════════════════════════════════════════ */

const serviceLinks = [
  { label: "UX / UI Design", to: "/services" },
  { label: "Website Development", to: "/services" },
  { label: "Mobile Apps", to: "/services" },
  { label: "E-Commerce & SaaS", to: "/services" },
  { label: "Hosting & DevOps", to: "/services" },
];

const companyLinks = [
  { label: "About Us", to: "/about" },
  { label: "Blog", to: "/blog" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/home#contact" },
];

/* ── Inline SVG icons ─────────────────────────────────────── */
const PhoneIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-[#808692]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);

const MailIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-[#808692]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
  </svg>
);

const MapPinIcon = () => (
  <svg className="h-4 w-4 shrink-0 mt-0.5 text-[#808692]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125ZM6.838 20.452H3.834V9h3.004v11.452Z" />
  </svg>
);

const XTwitterIcon = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
  </svg>
);

/* ── Toast banner ────────────────────────────────────────── */
const ToastBanner = ({ toast }: { toast: { type: "success" | "error"; msg: string } | null }) =>
  toast ? (
    <div
      className={`fixed top-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-all ${
        toast.type === "success" ? "bg-[#28E454]" : "bg-red-500"
      }`}
    >
      {toast.type === "success" && (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      )}
      {toast.msg}
    </div>
  ) : null;

/* ═══════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                             */
/* ═══════════════════════════════════════════════════════════ */

interface FooterProps {
  isMob?: boolean;
}

export const Footer: React.FC<FooterProps> = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const { actions } = useEtlynCms("newsletters-core");

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return showToast("error", "Please enter your email address");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return showToast("error", "Please enter a valid email");

    setIsSubmitting(true);
    try {
      actions.subscribeToNewsletter({ email, source: "etlyn-com" });
      showToast("success", "You're subscribed! Check your inbox.");
      setEmail("");
    } catch {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative mt-24 border-t border-[#DFDFE0]/60 bg-[#F8F9FB]">
      <ToastBanner toast={toast} />

      {/* ── Main content ─────────────────────────────────── */}
      <div className="relative mx-auto max-w-6xl px-6 pt-12 pb-8 sm:px-8 lg:px-12">
        {/* 4-column grid */}
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12">
          {/* Col 1 — Contact */}
          <div className="lg:col-span-4 space-y-4">
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#808692]">
              Contact
            </h4>

            <div className="space-y-2.5">
              <a
                href="tel:+14157450485"
                className="flex items-center gap-2.5 text-sm text-[#666D7C] transition hover:text-[#333D51]"
              >
                <PhoneIcon />
                +1 415 745 0485
              </a>
              <a
                href="mailto:info@etlyn.com"
                className="flex items-center gap-2.5 text-sm text-[#666D7C] transition hover:text-[#333D51]"
              >
                <MailIcon />
                info@etlyn.com
              </a>
              <div className="flex items-start gap-2.5 text-sm text-[#666D7C]">
                <MapPinIcon />
                <span>535 Mission St, San Francisco, CA 94105</span>
              </div>
            </div>
          </div>

          {/* Col 2 — Services */}
          <div className="lg:col-span-2">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-[#808692]">
              Services
            </h4>
            <ul className="space-y-2.5">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-sm text-[#666D7C] transition hover:text-[#0D2B7E]"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Company */}
          <div className="lg:col-span-2">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-[#808692]">
              Company
            </h4>
            <ul className="space-y-2.5">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-sm text-[#666D7C] transition hover:text-[#0D2B7E]"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Newsletter */}
          <div className="lg:col-span-4">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-[#808692]">
              Stay in the loop
            </h4>
            <p className="mb-4 text-sm leading-relaxed text-[#808692]">
              Weekly insights on building digital products that sell.
            </p>

            <form onSubmit={handleSubscribe} className="flex gap-0">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                className="min-w-0 flex-1 rounded-l-lg border border-[#DFDFE0] bg-white px-4 py-2.5 text-sm text-[#333D51] placeholder:text-[#808692]/60 outline-none transition focus:border-[#0D2B7E]/30 focus:ring-1 focus:ring-[#0D2B7E]/10 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="shrink-0 rounded-r-lg bg-[#0D2B7E] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0D2B7E]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <ArrowRightIcon />
                )}
              </button>
            </form>
          </div>
        </div>

        {/* ── Divider ────────────────────────────────────── */}
        <div className="mt-10 mb-6 h-px bg-[#DFDFE0]/60" />

        {/* ── Bottom bar ─────────────────────────────────── */}
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-xs text-[#808692]">
            &copy; {new Date().getFullYear()} Etlyn. All rights reserved.
          </p>

          <a href="/privacy" className="text-xs text-[#808692] underline-offset-4 hover:text-[#0D2B7E] hover:underline">
            Privacy policy
          </a>

          <div className="flex items-center gap-2.5">
            <a
              href="https://www.linkedin.com/company/etlyn"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-[#808692] transition hover:bg-[#EEF1FB] hover:text-[#0D2B7E]"
              aria-label="LinkedIn"
            >
              <LinkedInIcon />
            </a>
            <a
              href="https://x.com/Etlyn_LLC"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-[#808692] transition hover:bg-[#EEF1FB] hover:text-[#0D2B7E]"
              aria-label="X (Twitter)"
            >
              <XTwitterIcon />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
